import type { SFTPConnection, SftpFolderMount } from "types/interfaces/sftp";

export type FolderMountRow = SftpFolderMount & {
  id: string;
  subfolder_aliases: Record<string, string>;
};

export const SFTP_ALIAS_MAX_LENGTH = 255;

export function newFolderMountRow(): FolderMountRow {
  return {
    id: crypto.randomUUID(),
    label: "",
    prefix: "",
    subfolders: [],
    subfolder_aliases: {},
  };
}

export function folderMountRowsFromConnection(
  c: SFTPConnection | null,
): FolderMountRow[] {
  if (!c) return [newFolderMountRow()];
  const fm = c.folder_mounts;
  if (fm && fm.length > 0) {
    return fm.map((m) => ({
      id: crypto.randomUUID(),
      label: m.label ?? "",
      prefix: m.prefix ?? "",
      subfolders: [...(m.subfolders ?? [])],
      subfolder_aliases: { ...(m.subfolder_aliases ?? {}) },
    }));
  }
  return [
    {
      id: crypto.randomUUID(),
      label: "",
      prefix: c.folder ?? "",
      subfolders: [...(c.subfolders ?? [])],
      subfolder_aliases: {},
    },
  ];
}

/**
 * Canonical alias map (sorted keys, trimmed, no self/empty values) — must match
 * the backend's stored form so the dirty-check stays byte-stable.
 */
export function cleanSubfolderAliases(
  aliases: Record<string, string> | null | undefined,
  subfolders: string[],
): Record<string, string> {
  const out: Record<string, string> = {};
  if (!aliases) return out;
  const entries = subfolders
    .map((name) => [name, (aliases[name] ?? "").trim()] as const)
    .filter(([name, value]) => value.length > 0 && value !== name)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  for (const [name, value] of entries) out[name] = value;
  return out;
}

/** Why the SFTP name for `name` cannot be saved, or null when it is fine. */
export function subfolderAliasError(
  row: Pick<SftpFolderMount, "subfolders"> & {
    subfolder_aliases?: Record<string, string> | null;
  },
  name: string,
): string | null {
  const aliases = row.subfolder_aliases ?? {};
  const value = (aliases[name] ?? "").trim();
  if (!value || value === name) return null;
  if (value.includes("/")) return "SFTP name cannot contain '/'";
  if (value.length > SFTP_ALIAS_MAX_LENGTH) return "SFTP name is too long";
  if (row.subfolders.some((s) => s !== name && s === value)) {
    return "Conflicts with another subfolder";
  }
  if (
    row.subfolders.some((s) => s !== name && (aliases[s] ?? "").trim() === value)
  ) {
    return "Duplicate SFTP name";
  }
  return null;
}

export function folderMountRowAliasesValid(row: FolderMountRow): boolean {
  return row.subfolders.every((s) => subfolderAliasError(row, s) === null);
}

/** "Alias (physical)" when renamed, else the physical name. */
export function formatSubfolderDisplay(
  name: string,
  aliases?: Record<string, string> | null,
): string {
  const alias = (aliases?.[name] ?? "").trim();
  return alias && alias !== name ? `${alias} (${name})` : name;
}

export function folderMountsToPayload(
  rows: FolderMountRow[],
): SftpFolderMount[] {
  return rows.map(({ label, prefix, subfolders, subfolder_aliases }) => {
    const aliases = cleanSubfolderAliases(subfolder_aliases, subfolders);
    return {
      label: label.trim(),
      prefix: prefix.trim().replace(/^\/+/, ""),
      subfolders: [...subfolders],
      ...(Object.keys(aliases).length > 0
        ? { subfolder_aliases: aliases }
        : {}),
    };
  });
}

export function comparableFolderMountsFromConnection(
  c: SFTPConnection,
): string {
  return JSON.stringify(
    folderMountsToPayload(folderMountRowsFromConnection(c)),
  );
}

/** One row in the SFTP table “Bucket / Folder” column. */
export type BucketFolderDisplayLine = {
  sftpLabel: string;
  path: string;
  subfoldersSummary: string | null;
};

export function bucketFolderDisplayLines(
  conn: SFTPConnection,
): BucketFolderDisplayLine[] {
  const bucket = conn.bucket ?? "";
  const mounts = conn.folder_mounts;
  if (mounts && mounts.length > 0) {
    return mounts.map((m) => {
      const prefix = (m.prefix ?? "").trim().replace(/^\/+/, "");
      const path = prefix ? `${bucket}/${prefix}` : bucket;
      const subs = (m.subfolders ?? [])
        .map((s) => String(s).trim())
        .filter(Boolean)
        .map((s) => formatSubfolderDisplay(s, m.subfolder_aliases));
      return {
        sftpLabel: (m.label ?? "").trim(),
        path,
        subfoldersSummary: subs.length > 0 ? subs.join(", ") : null,
      };
    });
  }
  const folder = (conn.folder ?? "").trim().replace(/^\/+/, "");
  const path = folder ? `${bucket}/${folder}` : bucket;
  const subs = (conn.subfolders ?? [])
    .map((s) => String(s).trim())
    .filter(Boolean);
  return [
    {
      sftpLabel: "",
      path,
      subfoldersSummary: subs.length > 0 ? subs.join(", ") : null,
    },
  ];
}

/** Stable lowercase string for sorting the Bucket / Folder column. */
export function bucketFolderSortKey(conn: SFTPConnection): string {
  return bucketFolderDisplayLines(conn)
    .map((L) => `${L.sftpLabel}\x00${L.path}\x00${L.subfoldersSummary ?? ""}`)
    .join("\n")
    .toLowerCase();
}
